import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  MapPin,
  Calendar,
  Clock,
  Heart,
  UtensilsCrossed,
  Music2,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  BedDouble,
} from 'lucide-react';
import { buildGoogleMapsEmbedSrc } from '../lib/invitation/googleMapsEmbed';
import { InvitationFormattedText } from '../lib/invitation/invitationFormattedText';
import {
  CATERING_SECTION_INTRO_DEFAULT,
  CATERING_MAIN_TITLE,
  CATERING_MAIN_BODY_DEFAULT,
  CATERING_WARM_TITLE,
  CATERING_WARM_BODY_DEFAULT,
  CATERING_COLD_TITLE,
  CATERING_COLD_BODY_DEFAULT,
  CATERING_SWEET_TITLE,
  CATERING_SWEET_BODY_DEFAULT,
  CATERING_DRINKS_TITLE,
  CATERING_DRINKS_BODY_DEFAULT,
  MUSIC_INTRO_DEFAULT,
  MUSIC_PLAYLIST_CTA_DEFAULT,
  ACCOMMODATION_INFO_DEFAULT,
  musicSourceDescription,
  isLikelySpotifyPlaylistUrl,
} from '../lib/invitation/publicInvitationDisplay';

/** Sekční nadpisy pozvánky (Orchid) — mírně menší než původní Figma 39px */
const H2 = 'font-bold text-[#b70b9d] text-[30px] sm:text-[34px] leading-[1.2] text-center';
/** Podnadpisy sekce */
const SUB = 'font-medium text-[#999] text-[17px] sm:text-[19px] leading-[1.2] text-center max-w-3xl mx-auto';
/** Datum u „Bereme se“ */
const DATE_LARGE = 'font-light text-[#808080] text-[28px] md:text-[34px] leading-[1.2] text-center tracking-wide';

const SECTION_Y = 'py-12 sm:py-14 px-4';

const FORMATTED_INTRO_BODY =
  'font-medium text-[#999] text-[17px] sm:text-[19px] leading-[1.2]';
const FORMATTED_STORY_BODY = 'text-[#666] text-[16px] sm:text-[18px] font-medium leading-relaxed';
const FORMATTED_CARD_BODY = 'text-[#666] text-sm sm:text-base leading-relaxed';
/** Text u karet občerstvení — menší, čitelnější řádkování (viz sekce Občerstvení). */
const CATERING_INVITATION_BODY = 'text-[#666] text-[13px] sm:text-[14px] leading-[1.35]';

interface PublicConfig {
  showSchedule: boolean;
  showFaq: boolean;
  welcomeMessage: string;
  brideName?: string | null;
  groomName?: string | null;
  bridePhotoUrl?: string | null;
  groomPhotoUrl?: string | null;
  ceremonyTime?: string | null;
  ceremonyVenue?: string | null;
  fullAddress?: string | null;
  mapsUrl?: string | null;
  dressCode?: string | null;
  heroImageUrl?: string | null;
  storyText?: string | null;
  showCatering?: boolean;
  cateringIntro?: string | null;
  cateringShowMain?: boolean;
  cateringMainText?: string | null;
  cateringShowWarmBuffet?: boolean;
  cateringWarmBuffetText?: string | null;
  cateringShowColdBuffet?: boolean;
  cateringColdBuffetText?: string | null;
  cateringShowSweetBar?: boolean;
  cateringSweetBarText?: string | null;
  cateringShowDrinks?: boolean;
  cateringDrinksText?: string | null;
  cateringMainPhotoUrl?: string | null;
  cateringWarmPhotoUrl?: string | null;
  cateringColdPhotoUrl?: string | null;
  cateringSweetPhotoUrl?: string | null;
  cateringDrinksPhotoUrl?: string | null;
  showMusic?: boolean;
  musicIntro?: string | null;
  musicLiveDj?: boolean;
  musicFromSpeakers?: boolean;
  musicSpotifyUrl?: string | null;
  musicPlaylistCta?: string | null;
  showEntertainment?: boolean;
  entertainmentIntro?: string | null;
  showAccommodationInfo?: boolean;
  accommodationInfo?: string | null;
  accommodationMapsUrl?: string | null;
  showGuestDetailsForm?: boolean;
  guestFormIntro?: string | null;
  guestFormShowAccommodation?: boolean;
  guestFormShowAllergies?: boolean;
  guestFormShowChildren?: boolean;
  guestFormShowGuestNote?: boolean;
}

interface PublicWedding {
  title: string;
  date: string;
  location: string;
  welcome_message: string;
  config: PublicConfig;
}

interface ScheduleItem {
  id: string;
  start_time: string;
  end_time?: string;
  title: string;
  location?: string;
  notes?: string | null;
}

interface Note {
  id: string;
  title: string;
  content: string;
}

interface PublicActivityItem {
  id: string;
  title: string;
  description?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  is_all_day?: boolean | null;
}

interface Countdown {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function useCountdown(targetDate: string): Countdown {
  const calcRemaining = (): Countdown => {
    const now = new Date().getTime();
    const target = new Date(targetDate).getTime();
    const diff = Math.max(0, target - now);
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };
  };

  const [countdown, setCountdown] = useState<Countdown>(calcRemaining);

  useEffect(() => {
    const timer = setInterval(() => setCountdown(calcRemaining()), 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  return countdown;
}

/** Kompaktní odpočet — menší na mobilu i desktopu */
function CountdownBox({ value, label }: { value: number; label: string }) {
  return (
    <div
      className="bg-white rounded-[16px] px-4 py-3 sm:px-5 sm:py-4 flex flex-col items-center justify-center min-w-[4rem] sm:min-w-[4.5rem]"
      style={{ boxShadow: '0px 0px 40px 0px rgba(231,103,103,0.15)' }}
    >
      <span className="font-['Comfortaa',sans-serif] font-medium text-[#b70b9d] text-[28px] sm:text-[36px] md:text-[40px] leading-none tabular-nums">
        {String(value).padStart(2, '0')}
      </span>
      <span className="font-['Comfortaa',sans-serif] font-medium text-[#b70b9d] text-[14px] sm:text-[18px] mt-1 leading-none">
        {label}
      </span>
    </div>
  );
}

/** Dekorativní „věnec“ kolem portrétu — dvojitý kruh #f0bce3 (Orchid/700 ve Figmě) */
function CouplePhotoFrame({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="relative mx-auto w-[200px] h-[200px] sm:w-[275px] sm:h-[275px] shrink-0">
      <div
        className="absolute inset-0 rounded-full border-[3px] border-[#f0bce3] sm:border-4"
        style={{ boxShadow: '0 0 0 6px rgba(240, 188, 227, 0.35), 0 0 0 12px rgba(255, 255, 255, 0.9)' }}
      />
      <div className="absolute inset-[10px] sm:inset-[14px] rounded-full overflow-hidden border-2 border-white shadow-inner bg-white flex items-center justify-center">
        <img src={src} alt={alt} className="w-full h-full object-contain object-center" />
      </div>
    </div>
  );
}

function OrchidDivider() {
  return (
    <div className="flex items-center justify-center my-2 gap-3">
      <div className="h-px flex-1 max-w-[120px]" style={{ background: 'linear-gradient(to right, transparent, #e382d3)' }} />
      <Heart size={18} className="text-[#e382d3] shrink-0" fill="currentColor" />
      <div className="h-px flex-1 max-w-[120px]" style={{ background: 'linear-gradient(to left, transparent, #e382d3)' }} />
    </div>
  );
}

type PublicLoadFail = 'not_found' | 'server' | 'network' | 'wrong_response';

/** Odstraní náhodné znaky nového řádku v URL uložených v DB (jinak <img> / iframe padá nebo se nenačte). */
function sanitizePublicMediaUrl(url: string | null | undefined): string | null {
  if (url == null || typeof url !== 'string') return null;
  const t = url
    .replace(/%0d/gi, '')
    .replace(/%0a/gi, '')
    .replace(/[\r\n\t]+/g, '')
    .trim();
  return t.length > 0 ? t : null;
}

function CateringInvitationWidget({
  title,
  bodyText,
  defaultBody,
  imageUrl,
  bodyClassName,
}: {
  title: string;
  bodyText: string | null | undefined;
  defaultBody: string;
  imageUrl: string | null | undefined;
  bodyClassName: string;
}) {
  const photo = sanitizePublicMediaUrl(imageUrl);
  return (
    <div
      className={`flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-transparent bg-white transition-[transform,box-shadow] duration-200 ease-out hover:scale-[1.01] hover:shadow-lg ${
        photo ? '' : 'pb-5 sm:pb-6'
      }`}
      style={{ boxShadow: '0px 0px 28px 0px rgba(231,103,103,0.08)' }}
    >
      <div className="min-w-0 shrink-0 px-3 pt-2.5 sm:px-3.5 sm:pt-3">
        <h4 className="mb-2 shrink-0 font-bold leading-[1.25] text-[#b70b9d] text-sm sm:mb-2.5 sm:text-[15px]">
          {title}
        </h4>
        <InvitationFormattedText
          text={(bodyText && bodyText.trim()) || defaultBody}
          bodyClassName={bodyClassName}
          compact
        />
      </div>
      {photo ? (
        <>
          <div className="min-h-2 min-w-0 flex-1 basis-0 sm:min-h-2.5" aria-hidden />
          <div
            className="w-full shrink-0"
            style={{
              background:
                'linear-gradient(180deg, #ffffff 0%, #fff8fc 14%, #fdeef8 42%, #efd6e6 100%)',
            }}
          >
            <div className="relative h-[168px] w-full overflow-hidden sm:h-[188px] lg:h-[200px]">
              <img
                src={photo}
                alt=""
                className="absolute inset-0 h-full w-full object-cover object-bottom"
              />
            </div>
          </div>
        </>
      ) : (
        <div className="min-h-0 flex-1" aria-hidden />
      )}
    </div>
  );
}

type PublicConfigCatering = Pick<
  PublicConfig,
  | 'cateringShowMain'
  | 'cateringMainText'
  | 'cateringMainPhotoUrl'
  | 'cateringShowWarmBuffet'
  | 'cateringWarmBuffetText'
  | 'cateringWarmPhotoUrl'
  | 'cateringShowColdBuffet'
  | 'cateringColdBuffetText'
  | 'cateringColdPhotoUrl'
  | 'cateringShowSweetBar'
  | 'cateringSweetBarText'
  | 'cateringSweetPhotoUrl'
  | 'cateringShowDrinks'
  | 'cateringDrinksText'
  | 'cateringDrinksPhotoUrl'
>;

function cateringInvitationBlockEntries(cfg: PublicConfigCatering): { key: string; el: React.ReactElement }[] {
  const blocks: { key: string; el: React.ReactElement }[] = [];
  if (cfg.cateringShowMain) {
    blocks.push({
      key: 'main',
      el: (
        <CateringInvitationWidget
          title={CATERING_MAIN_TITLE}
          bodyText={cfg.cateringMainText}
          defaultBody={CATERING_MAIN_BODY_DEFAULT}
          imageUrl={cfg.cateringMainPhotoUrl}
          bodyClassName={CATERING_INVITATION_BODY}
        />
      ),
    });
  }
  if (cfg.cateringShowWarmBuffet) {
    blocks.push({
      key: 'warm',
      el: (
        <CateringInvitationWidget
          title={CATERING_WARM_TITLE}
          bodyText={cfg.cateringWarmBuffetText}
          defaultBody={CATERING_WARM_BODY_DEFAULT}
          imageUrl={cfg.cateringWarmPhotoUrl}
          bodyClassName={CATERING_INVITATION_BODY}
        />
      ),
    });
  }
  if (cfg.cateringShowColdBuffet) {
    blocks.push({
      key: 'cold',
      el: (
        <CateringInvitationWidget
          title={CATERING_COLD_TITLE}
          bodyText={cfg.cateringColdBuffetText}
          defaultBody={CATERING_COLD_BODY_DEFAULT}
          imageUrl={cfg.cateringColdPhotoUrl}
          bodyClassName={CATERING_INVITATION_BODY}
        />
      ),
    });
  }
  if (cfg.cateringShowSweetBar) {
    blocks.push({
      key: 'sweet',
      el: (
        <CateringInvitationWidget
          title={CATERING_SWEET_TITLE}
          bodyText={cfg.cateringSweetBarText}
          defaultBody={CATERING_SWEET_BODY_DEFAULT}
          imageUrl={cfg.cateringSweetPhotoUrl}
          bodyClassName={CATERING_INVITATION_BODY}
        />
      ),
    });
  }
  if (cfg.cateringShowDrinks) {
    blocks.push({
      key: 'drinks',
      el: (
        <CateringInvitationWidget
          title={CATERING_DRINKS_TITLE}
          bodyText={cfg.cateringDrinksText}
          defaultBody={CATERING_DRINKS_BODY_DEFAULT}
          imageUrl={cfg.cateringDrinksPhotoUrl}
          bodyClassName={CATERING_INVITATION_BODY}
        />
      ),
    });
  }
  return blocks;
}

const GUEST_FORM_INTRO_DEFAULT =
  'Budeme rádi za pár informací – pomůže nám to při přípravě. Vyplnění je dobrovolné kromě jména.';

function GuestInvitationExtrasSection({ slug, cfg }: { slug: string; cfg: PublicConfig }) {
  const showAcc = !!cfg.guestFormShowAccommodation;
  const showAlg = !!cfg.guestFormShowAllergies;
  const showCh = !!cfg.guestFormShowChildren;
  const showNote = !!cfg.guestFormShowGuestNote;
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [wantsAcc, setWantsAcc] = useState<'yes' | 'no' | 'unsure' | ''>('');
  const [allergies, setAllergies] = useState('');
  const [guestMessage, setGuestMessage] = useState('');
  const [childrenMode, setChildrenMode] = useState<'yes' | 'no' | 'unsure' | ''>('');
  const [childrenCount, setChildrenCount] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const intro = (cfg.guestFormIntro && cfg.guestFormIntro.trim()) || GUEST_FORM_INTRO_DEFAULT;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!firstName.trim() || !lastName.trim()) {
      setErr('Vyplňte prosím jméno a příjmení.');
      return;
    }
    if (
      showCh &&
      (childrenMode === 'yes' || childrenMode === 'unsure') &&
      (childrenCount < 1 || childrenCount > 50)
    ) {
      setErr('Zadejte počet dětí (1–50), nebo zrušte volbu u dětí.');
      return;
    }

    const payload: Record<string, unknown> = {
      slug,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
    };
    if (showAcc && wantsAcc) payload.wantsAccommodation = wantsAcc;
    if (showAlg) payload.allergies = allergies.trim() || '';
    if (showCh && childrenMode) {
      payload.childrenMode = childrenMode;
      payload.childrenCount = childrenMode === 'no' ? 0 : childrenCount;
    }
    if (showAcc || showAlg || showCh || showNote) {
      payload.message = guestMessage.trim() || '';
    }

    setSubmitting(true);
    try {
      const r = await fetch('/api/public/invitation-response', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErr(typeof data.error === 'string' ? data.error : 'Odeslání se nepodařilo.');
        return;
      }
      setDone(true);
      setFirstName('');
      setLastName('');
      setWantsAcc('');
      setAllergies('');
      setGuestMessage('');
      setChildrenMode('');
      setChildrenCount(1);
    } catch {
      setErr('Síťová chyba. Zkuste to prosím znovu.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={SECTION_Y}>
      <div className="max-w-[800px] mx-auto px-1 sm:px-0">
        <h2 className={`${H2} mb-1 flex items-center justify-center gap-2 sm:gap-3`}>
          <MessageSquare size={28} className="text-[#e382d3] shrink-0" aria-hidden />
          Pár informací pro nás
        </h2>
        <OrchidDivider />
        <div className="mt-8 w-full px-1 sm:px-0">
          <InvitationFormattedText justify text={intro} bodyClassName={FORMATTED_INTRO_BODY} />
        </div>

        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="mt-8 w-full flex items-center justify-between gap-3 rounded-[20px] bg-white px-4 py-3 sm:px-6 sm:py-4 text-left transition-shadow hover:opacity-95"
          style={{ boxShadow: '0px 0px 40px 0px rgba(231,103,103,0.1)' }}
        >
          <span className="font-bold text-[#b70b9d] text-base sm:text-lg">
            Vyplnit formulář (volitelné)
          </span>
          {open ? (
            <ChevronDown className="shrink-0 text-[#e382d3]" size={22} aria-hidden />
          ) : (
            <ChevronRight className="shrink-0 text-[#e382d3]" size={22} aria-hidden />
          )}
        </button>

        {open && (
          <div
            className="mt-6 bg-white rounded-[20px] p-6 sm:p-8"
            style={{ boxShadow: '0px 0px 40px 0px rgba(231,103,103,0.1)' }}
          >
            {done && (
              <div
                className="mb-6 rounded-2xl border-2 border-[#81c784] bg-gradient-to-b from-[#e8f5e9] to-[#c8e6c9]/90 px-5 py-5 sm:px-6 sm:py-6 text-center shadow-md"
                role="status"
                aria-live="polite"
              >
                <p className="text-lg sm:text-xl font-bold text-[#1b5e20] tracking-tight">Děkujeme!</p>
                <p className="mt-2 text-sm sm:text-base font-semibold text-[#2e7d32]">
                  Údaje jsme úspěšně uložili.
                </p>
              </div>
            )}
            <form onSubmit={onSubmit} className="space-y-4">
              <p className="text-xs sm:text-sm text-[#888] leading-relaxed -mt-1 mb-1">
                Kromě jména a příjmení jsou jednotlivé otázky dobrovolné — můžete je vynechat.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-[#808080]">Jméno</span>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoComplete="given-name"
                    className="mt-1 w-full rounded-xl border border-[#e8e8e8] px-3 py-2.5 text-[#4d4d4d] text-sm outline-none focus:ring-2 focus:ring-[#e382d3]/50"
                    required
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-[#808080]">Příjmení</span>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    autoComplete="family-name"
                    className="mt-1 w-full rounded-xl border border-[#e8e8e8] px-3 py-2.5 text-[#4d4d4d] text-sm outline-none focus:ring-2 focus:ring-[#e382d3]/50"
                    required
                  />
                </label>
              </div>

              {showAcc && (
                <fieldset className="space-y-2">
                  <legend className="text-sm font-medium text-[#4d4d4d] mb-1">
                    Máte zájem zajistit ubytování?
                  </legend>
                  <div className="flex flex-wrap gap-3">
                    {(
                      [
                        ['yes', 'Ano'],
                        ['no', 'Ne'],
                        ['unsure', 'Ještě nevím'],
                      ] as const
                    ).map(([val, label]) => (
                      <label key={val} className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="wantsAcc"
                          checked={wantsAcc === val}
                          onChange={() => setWantsAcc(val)}
                          className="text-[#b70b9d] focus:ring-[#e382d3]"
                        />
                        <span className="text-sm text-[#666]">{label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}

              {showAlg && (
                <label className="block">
                  <span className="text-sm font-medium text-[#4d4d4d] leading-snug block">
                    Máte požadavek na úpravy občerstvení z důvodu alergií nebo dietního omezení?
                  </span>
                  <textarea
                    value={allergies}
                    onChange={(e) => setAllergies(e.target.value)}
                    rows={3}
                    placeholder="Např. bez ořechů, bez lepku…"
                    className="mt-1 w-full rounded-xl border border-[#e8e8e8] px-3 py-2.5 text-[#4d4d4d] text-sm outline-none focus:ring-2 focus:ring-[#e382d3]/50 resize-y"
                  />
                </label>
              )}

              {showCh && (
                <fieldset className="space-y-3">
                  <legend className="text-sm font-medium text-[#4d4d4d]">
                    Zúčastníte se svatby i s dětmi?
                  </legend>
                  <div className="flex flex-wrap gap-4">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="kids"
                        checked={childrenMode === 'yes'}
                        onChange={() => setChildrenMode('yes')}
                        className="text-[#b70b9d]"
                      />
                      <span className="text-sm text-[#666]">Ano</span>
                    </label>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="kids"
                        checked={childrenMode === 'no'}
                        onChange={() => setChildrenMode('no')}
                        className="text-[#b70b9d]"
                      />
                      <span className="text-sm text-[#666]">Ne</span>
                    </label>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="kids"
                        checked={childrenMode === 'unsure'}
                        onChange={() => setChildrenMode('unsure')}
                        className="text-[#b70b9d]"
                      />
                      <span className="text-sm text-[#666]">Ještě nevím</span>
                    </label>
                  </div>
                  {(childrenMode === 'yes' || childrenMode === 'unsure') && (
                    <label className="inline-flex flex-wrap items-center gap-2">
                      <span className="text-sm text-[#666]">Počet dětí</span>
                      <input
                        type="number"
                        min={1}
                        max={50}
                        value={childrenCount}
                        onChange={(e) => setChildrenCount(parseInt(e.target.value, 10) || 1)}
                        className="w-20 rounded-xl border border-[#e8e8e8] px-2 py-1.5 text-sm"
                      />
                    </label>
                  )}
                </fieldset>
              )}

              {(showAcc || showAlg || showCh || showNote) && (
                <label className="block">
                  <span className="text-sm font-medium text-[#4d4d4d]">Poznámka</span>
                  <textarea
                    value={guestMessage}
                    onChange={(e) => setGuestMessage(e.target.value)}
                    rows={3}
                    placeholder="Volitelný text – cokoli dalšího, co pro vás chceme vědět…"
                    className="mt-1 w-full rounded-xl border border-[#e8e8e8] px-3 py-2.5 text-[#4d4d4d] text-sm outline-none focus:ring-2 focus:ring-[#e382d3]/50 resize-y"
                  />
                </label>
              )}

              {err && <p className="text-sm text-red-600">{err}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto rounded-xl bg-[#b70b9d] hover:bg-[#9a0a85] text-white font-bold text-sm py-3 px-8 transition-colors disabled:opacity-60"
              >
                {submitting ? 'Odesílám…' : 'Odeslat'}
              </button>
            </form>
          </div>
        )}
      </div>
    </section>
  );
}

export default function PublicPage() {
  const { slug } = useParams<{ slug: string }>();
  const [wedding, setWedding] = useState<PublicWedding | null>(null);
  const [schedule, setSchedule] = useState<ScheduleItem[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [activities, setActivities] = useState<PublicActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFail, setLoadFail] = useState<PublicLoadFail | null>(null);
  const countdown = useCountdown(wedding?.date ?? '');

  /** Musí být před jakýmkoli `return` — jinak se po načtení dat změní počet hooků a React spadne (šedá stránka). */
  const weddingDate = wedding?.date
    ? new Date(wedding.date).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  const mapEmbedSrc = useMemo(() => {
    if (!wedding) return null;
    const cfg = wedding.config;
    return buildGoogleMapsEmbedSrc({
      fullAddress: cfg.fullAddress,
      ceremonyVenue: cfg.ceremonyVenue,
      weddingLocation: wedding.location,
      mapsUrl: cfg.mapsUrl,
    });
  }, [
    wedding?.config?.fullAddress,
    wedding?.config?.ceremonyVenue,
    wedding?.config?.mapsUrl,
    wedding?.location,
  ]);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setLoadFail(null);
    setWedding(null);
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/public/${encodeURIComponent(slug)}`);
        const ct = r.headers.get('content-type') || '';
        const data = await r.json().catch(() => ({}));
        if (cancelled) return;

        // SPA fallback někdy vrátí index.html (200 + text/html) místo API → vypadá to jako „nenalezeno“
        if (!ct.includes('application/json')) {
          setLoadFail('wrong_response');
          setWedding(null);
          setActivities([]);
          return;
        }

        if (data.wedding) {
          setLoadFail(null);
          setWedding(data.wedding);
          setSchedule(data.schedule ?? []);
          setNotes(data.notes ?? []);
          setActivities(data.activities ?? []);
          return;
        }

        setWedding(null);
        setActivities([]);
        if (r.status >= 500) setLoadFail('server');
        else if (!r.ok && r.status !== 404) setLoadFail('server');
        else setLoadFail('not_found');
      } catch {
        if (!cancelled) {
          setWedding(null);
          setActivities([]);
          setLoadFail('network');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fff3fb]">
        <Heart className="animate-pulse text-[#b70b9d]" size={48} fill="currentColor" />
      </div>
    );
  }

  if (!wedding) {
    const techBox = (
      <div className="text-left w-full rounded-2xl bg-amber-50/90 border border-amber-200 px-5 py-4 text-sm text-amber-950 font-['Comfortaa',sans-serif] mt-4">
        <p className="font-semibold mb-2">Pro majitele nasazení (Vercel)</p>
        <ul className="list-disc list-inside space-y-1.5">
          <li>
            V <strong>Vercel → Project → Settings → Environment Variables</strong> musí být nastavené{' '}
            <code className="text-xs bg-white/80 px-1 rounded">SUPABASE_SERVICE_ROLE_KEY</code> a URL{' '}
            <code className="text-xs bg-white/80 px-1 rounded">SUPABASE_URL</code> nebo{' '}
            <code className="text-xs bg-white/80 px-1 rounded">VITE_SUPABASE_URL</code> (stejný projekt jako v Supabase, kde máš tabulku{' '}
            <code className="text-xs bg-white/80 px-1 rounded">weddings</code>).
          </li>
          <li>
            Ověření: v prohlížeči otevři na <strong>stejné doméně</strong> jako aplikace{' '}
            <code className="text-xs bg-white/80 px-1 rounded break-all">/api/health</code> — u{' '}
            <code className="text-xs bg-white/80 px-1 rounded">serviceRole</code> musí být „configured“.
          </li>
          <li>
            Přímo API:{' '}
            <code className="text-xs bg-white/80 px-1 rounded break-all">
              /api/public/{slug ? encodeURIComponent(slug) : 'tvuj-slug'}
            </code>{' '}
            — očekává se JSON s polem <code className="text-xs bg-white/80 px-1 rounded">wedding</code>, ne HTML stránka.
          </li>
        </ul>
      </div>
    );

    if (loadFail === 'server' || loadFail === 'network' || loadFail === 'wrong_response') {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-[#fff3fb] p-6 text-center max-w-lg mx-auto">
          <Heart size={48} className="text-[#b70b9d] mb-4" />
          <h1 className="font-['Comfortaa',sans-serif] font-bold text-[#b70b9d] text-2xl mb-2">
            Pozvánku se nepodařilo načíst (technická chyba)
          </h1>
          <p className="font-['Comfortaa',sans-serif] text-[#808080] text-sm mb-2">
            {loadFail === 'network'
              ? 'Síťová chyba nebo server neodpověděl. Zkus to znovu za chvíli.'
              : loadFail === 'wrong_response'
                ? 'Server vrátil stránku místo dat — často špatné směrování API na hostingu.'
                : 'Chyba na serveru (např. chybí přístup k databázi).'}
          </p>
          {techBox}
        </div>
      );
    }

    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#fff3fb] p-6 text-center max-w-lg mx-auto">
        <Heart size={48} className="text-[#b70b9d] mb-4" />
        <h1 className="font-['Comfortaa',sans-serif] font-bold text-[#b70b9d] text-3xl mb-2">Pozvánka nenalezena</h1>
        <p className="font-['Comfortaa',sans-serif] text-[#808080] mb-6">
          Odkaz není platný, pozvánka je vypnutá, nebo se slug v odkazu neshoduje s tím, co je uložené v aplikaci (tabulka{' '}
          <code className="text-xs bg-white/60 px-1 rounded">weddings</code> → <code className="text-xs bg-white/60 px-1 rounded">public_slug</code>).
        </p>
        <div className="text-left w-full rounded-2xl bg-white/80 border border-[#f0bce3]/60 px-5 py-4 text-sm text-[#666] font-['Comfortaa',sans-serif]">
          <p className="font-semibold text-[#b70b9d] mb-2">Pořadatelé — jak to opravit</p>
          <ol className="list-decimal list-inside space-y-2">
            <li>
              V aplikaci: <strong>Nastavení → Pozvánka</strong> zapněte <strong>Zapnout veřejnou pozvánku</strong>.
            </li>
            <li>
              Do pole <strong>URL pozvánky (slug)</strong> zadejte přesně část za <code className="text-xs bg-[#fff3fb] px-1 rounded">/pozvanka/</code> (např.{' '}
              <code className="text-xs bg-[#fff3fb] px-1 rounded">maki-kuki</code>) a dole klikněte <strong>Uložit pozvánku</strong>.
            </li>
            <li>
              Ověřte odkaz pod polem slug — musí být stejný jako adresa v prohlížeči. V Supabase musí u dané svatby být stejný{' '}
              <code className="text-xs bg-[#fff3fb] px-1 rounded">public_slug</code> a <code className="text-xs bg-[#fff3fb] px-1 rounded">public_enabled</code> = true.
            </li>
          </ol>
        </div>
        <p className="text-xs text-[#999] mt-4 font-['Comfortaa',sans-serif]">
          Pořád nic? Otevři v prohlížeči <code className="bg-white/60 px-1 rounded">/api/health</code> a{' '}
          <code className="bg-white/60 px-1 rounded break-all">/api/public/{slug || 'slug'}</code> — pokud nevidíš JSON, jde o nasazení / proměnné prostředí.
        </p>
      </div>
    );
  }

  const cfg = wedding.config;
  const ceremonyBg = sanitizePublicMediaUrl(cfg.heroImageUrl);
  const bridePhotoSafe = sanitizePublicMediaUrl(cfg.bridePhotoUrl);
  const groomPhotoSafe = sanitizePublicMediaUrl(cfg.groomPhotoUrl);
  const showCeremonyBlock = !!(cfg.ceremonyVenue || cfg.ceremonyTime || cfg.fullAddress || cfg.dressCode || mapEmbedSrc);
  const showCeremonyPhotoCard = !!(cfg.ceremonyVenue || cfg.ceremonyTime || cfg.fullAddress || cfg.mapsUrl);
  const showDetailsCard = !!(cfg.dressCode || wedding.date || cfg.ceremonyTime || cfg.fullAddress);
  const storyPhotos = [bridePhotoSafe, groomPhotoSafe, ceremonyBg].filter(Boolean) as string[];

  const showCateringSection =
    !!cfg.showCatering &&
    (!!cfg.cateringShowMain ||
      !!cfg.cateringShowWarmBuffet ||
      !!cfg.cateringShowColdBuffet ||
      !!cfg.cateringShowSweetBar ||
      !!cfg.cateringShowDrinks);

  const musicSourceLine = musicSourceDescription(!!cfg.musicLiveDj, !!cfg.musicFromSpeakers);
  const spotifyUrlTrim = (cfg.musicSpotifyUrl && cfg.musicSpotifyUrl.trim()) || '';
  const showMusicSection = !!cfg.showMusic;
  const showEntertainmentSection = !!cfg.showEntertainment && activities.length > 0;

  const showGuestFormSection =
    !!cfg.showGuestDetailsForm &&
    (!!cfg.guestFormShowAccommodation ||
      !!cfg.guestFormShowAllergies ||
      !!cfg.guestFormShowChildren ||
      !!cfg.guestFormShowGuestNote);

  return (
    <div className="min-h-screen bg-[#fff3fb]" style={{ fontFamily: "'Comfortaa', sans-serif" }}>
      {/* ── HERO (Figma: full-bleed foto, 50% overlay, Allura jména) ── */}
      <section className="relative min-h-screen flex flex-col items-center justify-center text-center overflow-hidden">
        {ceremonyBg ? (
          <img src={ceremonyBg} alt="" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
        ) : (
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(135deg, #b70b9d 0%, #e382d3 50%, #f0bce3 100%)' }}
          />
        )}
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 px-6 space-y-5 max-w-4xl">
          <p className="text-white/85 font-medium tracking-[0.35em] uppercase text-xs sm:text-sm">SVATBA</p>
          <h1
            className="text-white leading-[1.05] px-2"
            style={{ fontFamily: "'Allura', cursive", fontSize: 'clamp(3rem, 12vw, 7.5rem)' }}
          >
            {cfg.brideName || 'Nevěsta'} &amp; {cfg.groomName || 'Ženich'}
          </h1>
          <div className="flex items-center justify-center gap-4 pt-1">
            <div className="h-px w-20 bg-white/50" />
            <Heart size={18} className="text-white/85" fill="currentColor" />
            <div className="h-px w-20 bg-white/50" />
          </div>
          {wedding.date && (
            <p className="text-white/95 font-light text-xl sm:text-2xl tracking-[0.2em] font-['Comfortaa',sans-serif]">
              {weddingDate}
            </p>
          )}
          {wedding.location && (
            <p className="text-white/75 flex items-center justify-center gap-2 text-base font-['Comfortaa',sans-serif]">
              <MapPin size={18} className="shrink-0 opacity-90" /> {wedding.location}
            </p>
          )}
        </div>
      </section>

      {/* ── MEET THE HAPPY COUPLE (Figma: bílá karta 30px radius, podnadpis #999, věnce u fotek) ── */}
      {(cfg.brideName || cfg.groomName) && (
        <section className="py-12 sm:py-16 px-4">
          <div
            className="max-w-[1040px] mx-auto bg-white rounded-[30px] px-6 sm:px-12 md:px-16 py-10 sm:py-14 text-center"
            style={{ boxShadow: '0px 0px 40px 0px rgba(231,103,103,0.15)' }}
          >
            <h2 className={`${H2} mb-1`}>Seznamte se se šťastným párem</h2>
            <OrchidDivider />
            {cfg.welcomeMessage && (
              <div className="mt-6 mb-10 text-center w-full px-2 sm:px-4">
                <div className="w-full max-w-[920px] mx-auto text-center">
                  <InvitationFormattedText text={cfg.welcomeMessage} bodyClassName={FORMATTED_INTRO_BODY} />
                </div>
              </div>
            )}
            <div className="flex flex-col lg:flex-row gap-6 lg:gap-16 xl:gap-20 justify-center items-center">
              {[
                { name: cfg.brideName, photo: bridePhotoSafe, label: 'Nevěsta' },
                { name: cfg.groomName, photo: groomPhotoSafe, label: 'Ženich' },
              ].map((person) =>
                person.name ? (
                  <div
                    key={person.label}
                    className="flex flex-col items-center gap-5 w-full max-w-sm mx-auto lg:w-auto lg:max-w-none lg:mx-0 lg:min-w-0 shrink-0 lg:px-2"
                  >
                    {person.photo ? (
                      <CouplePhotoFrame src={person.photo} alt={person.name} />
                    ) : (
                      <div className="relative mx-auto w-[200px] h-[200px] sm:w-[275px] sm:h-[275px] rounded-full bg-[#f0bce3]/35 flex items-center justify-center border-4 border-[#f0bce3] shadow-[0_0_40px_rgba(231,103,103,0.12)]">
                        <Heart size={56} className="text-[#e382d3]" fill="currentColor" />
                      </div>
                    )}
                    <p
                      className="text-[#a40781] text-[48px] sm:text-[56px] leading-tight text-center px-2"
                      style={{ fontFamily: "'Allura', cursive" }}
                    >
                      {person.name}
                    </p>
                    <span className="text-[#808080] text-xs font-bold tracking-[0.25em] uppercase">{person.label}</span>
                  </div>
                ) : null,
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── BEREME SE (Figma: „We’re Getting Married“ — datum Light 40px, odpočet řada boxů) ── */}
      <section className={`${SECTION_Y} text-center`}>
        <h2 className={`${H2} mb-1`}>Bereme se!</h2>
        <OrchidDivider />
        {wedding.date && <p className={`${DATE_LARGE} mt-6 mb-7`}>{weddingDate}</p>}
        {wedding.date && (
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            <CountdownBox value={countdown.days} label="D" />
            <CountdownBox value={countdown.hours} label="H" />
            <CountdownBox value={countdown.minutes} label="M" />
            <CountdownBox value={countdown.seconds} label="S" />
          </div>
        )}
      </section>

      {/* ── KDE A KDY — jedna karta na celou šířku (foto/gradient + všechny údaje bez duplicit) */}
      {showCeremonyBlock && (
        <section className={SECTION_Y}>
          <div className="max-w-[1040px] mx-auto">
            <h2 className={`${H2} mb-1`}>Kde a kdy</h2>
            <OrchidDivider />

            {(showCeremonyPhotoCard || showDetailsCard) && (
            <div className="mt-10 w-full">
              <div className="relative overflow-hidden rounded-[30px] flex flex-col justify-start w-full">
                  {ceremonyBg ? (
                    <img src={ceremonyBg} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div
                      className="absolute inset-0"
                      style={{ background: 'linear-gradient(145deg, #b70b9d, #e382d3)' }}
                    />
                  )}
                  <div className="absolute inset-0 bg-black/30" />
                  <div className="relative z-10 p-6 sm:p-8 md:p-10 flex flex-col gap-4 sm:gap-5 text-white text-left max-w-none">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                        <span className="text-3xl sm:text-4xl shrink-0 leading-none select-none" aria-hidden>
                          💍
                        </span>
                        <h3 className="font-bold text-[22px] sm:text-[26px] md:text-[28px] leading-tight min-w-0">
                          Svatební obřad
                        </h3>
                      </div>
                      <div className="h-px w-full bg-white/40" />
                    </div>

                    {(wedding.date || cfg.ceremonyTime) && (
                      <div
                        className={`grid grid-cols-1 gap-4 sm:gap-5 ${
                          wedding.date && cfg.ceremonyTime ? 'sm:grid-cols-2' : ''
                        }`}
                      >
                        {wedding.date && (
                          <div className="flex items-start gap-3 sm:gap-4">
                            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/15 border border-white/25 flex items-center justify-center shrink-0">
                              <Calendar size={18} className="text-white opacity-95" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-white/75 text-xs sm:text-sm">Datum</p>
                              <p className="font-bold text-base sm:text-lg leading-snug">{weddingDate}</p>
                            </div>
                          </div>
                        )}
                        {cfg.ceremonyTime && (
                          <div className="flex items-start gap-3 sm:gap-4">
                            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/15 border border-white/25 flex items-center justify-center shrink-0">
                              <Clock size={18} className="text-white opacity-95" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-white/75 text-xs sm:text-sm">Čas obřadu</p>
                              <p className="font-bold text-base sm:text-lg leading-snug">{cfg.ceremonyTime}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {cfg.ceremonyVenue ? (
                      <p className="font-bold text-[17px] sm:text-[20px] md:text-[22px] leading-snug">{cfg.ceremonyVenue}</p>
                    ) : cfg.fullAddress ? (
                      <p className="font-bold text-[17px] sm:text-[20px] md:text-[22px] leading-snug">{cfg.fullAddress}</p>
                    ) : null}

                    {cfg.ceremonyVenue &&
                      cfg.fullAddress &&
                      cfg.fullAddress.trim().toLowerCase() !== (cfg.ceremonyVenue ?? '').trim().toLowerCase() && (
                        <div className="flex items-start gap-3 sm:gap-4">
                          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/15 border border-white/25 flex items-center justify-center shrink-0">
                            <MapPin size={18} className="text-white opacity-95" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-white/75 text-xs sm:text-sm">Adresa</p>
                            <p className="font-bold text-base sm:text-lg leading-snug">{cfg.fullAddress}</p>
                          </div>
                        </div>
                      )}

                    {cfg.dressCode && (
                      <div className="flex items-start gap-3 sm:gap-4">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/15 border border-white/25 flex items-center justify-center shrink-0 text-base" aria-hidden>
                          👗
                        </div>
                        <div className="min-w-0">
                          <p className="text-white/75 text-xs sm:text-sm">Dress code / oblečení</p>
                          <p className="font-bold text-base sm:text-lg leading-snug">{cfg.dressCode}</p>
                        </div>
                      </div>
                    )}

                    {cfg.mapsUrl && (
                      <a
                        href={cfg.mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 bg-white text-[#e382d3] font-bold text-sm sm:text-base py-3 px-6 sm:px-8 rounded-[10px] hover:bg-white/95 transition-colors w-full sm:w-auto sm:self-start shadow-lg mt-1"
                      >
                        <MapPin size={18} /> Otevřít mapu
                      </a>
                    )}
                  </div>
                </div>
            </div>
            )}

            {mapEmbedSrc && (
              <div
                className="mt-10 rounded-[24px] overflow-hidden border border-[#f0bce3] bg-white h-[240px] sm:h-[300px] md:h-[360px]"
                style={{ boxShadow: '0px 0px 40px 0px rgba(231,103,103,0.15)' }}
              >
                <iframe
                  title="Mapa místa konání"
                  src={mapEmbedSrc}
                  className="w-full h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── NÁŠ PŘÍBĚH — Figma: asymetrická mřížka s fotkami, pokud jsou k dispozici ── */}
      {cfg.storyText && (
        <section className={SECTION_Y}>
          <div
            className="max-w-[800px] mx-auto bg-white rounded-[30px] px-6 sm:px-12 py-10 sm:py-12"
            style={{ boxShadow: '0px 0px 40px 0px rgba(231,103,103,0.15)' }}
          >
            <h2 className={`${H2} mb-1`}>Náš příběh</h2>
            <OrchidDivider />
            {storyPhotos.length >= 2 && (
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                {storyPhotos.slice(0, 3).map((src, i) => (
                  <div
                    key={i}
                    className={`rounded-[12px] overflow-hidden aspect-[4/5] ${i === 0 ? 'sm:row-span-1' : ''}`}
                  >
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
            <div className="mt-8 text-center w-full px-2">
              <div className="inline-block text-left max-w-3xl">
                <InvitationFormattedText text={cfg.storyText} bodyClassName={FORMATTED_STORY_BODY} />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── HARMONOGRAM ── */}
      {cfg.showSchedule && schedule.length > 0 && (
        <section className={SECTION_Y}>
          <div className="max-w-[800px] mx-auto">
            <h2 className={`${H2} mb-1`}>Harmonogram dne</h2>
            <OrchidDivider />
            <div className="mt-8 space-y-0">
              {schedule.map((item, idx) => (
                <div key={item.id} className="flex gap-4 sm:gap-5">
                  <div className="flex flex-col items-center">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 z-10 shadow-sm"
                      style={{ background: 'linear-gradient(135deg, #b70b9d, #e382d3)' }}
                    >
                      <Heart size={16} className="text-white" fill="currentColor" />
                    </div>
                    {idx < schedule.length - 1 && (
                      <div className="w-0.5 flex-1 bg-[#f0bce3] my-1 min-h-[24px]" />
                    )}
                  </div>
                  <div className="pb-4 sm:pb-5 flex-1 min-w-0">
                    <div
                      className="bg-white rounded-[16px] px-5 py-4 sm:px-6 sm:py-4"
                      style={{ boxShadow: '0px 0px 16px 0px rgba(231,103,103,0.09)' }}
                    >
                      <div className="flex items-baseline gap-3 flex-wrap">
                        <span className="text-[#b70b9d] font-bold text-base tabular-nums shrink-0">
                          {item.start_time?.slice(0, 5)}
                          {item.end_time && (
                            <span className="text-[#b3b3b3] font-normal"> – {item.end_time.slice(0, 5)}</span>
                          )}
                        </span>
                        <h4 className="font-semibold text-[#4d4d4d] text-base sm:text-lg leading-snug min-w-0">
                          {item.title}
                        </h4>
                      </div>
                      {item.location && (
                        <p className="text-[#808080] text-sm mt-1.5 flex items-center gap-1.5">
                          <MapPin size={14} /> {item.location}
                        </p>
                      )}
                      {item.notes && item.notes.trim() && (
                        <p className="text-[#666] text-sm sm:text-[15px] mt-2 leading-relaxed whitespace-pre-line">
                          {item.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── OBČERSTVENÍ ── */}
      {showCateringSection && (
        <section className={SECTION_Y}>
          <div className="max-w-[1040px] mx-auto px-2 sm:px-0">
            <h2 className={`${H2} mb-1 flex items-center justify-center gap-2 sm:gap-3`}>
              <UtensilsCrossed size={28} className="text-[#e382d3] shrink-0" aria-hidden />
              Občerstvení
            </h2>
            <OrchidDivider />
            <div className="mt-8 text-center w-full px-2">
              <div className="inline-block text-left max-w-3xl mx-auto">
                <InvitationFormattedText
                  text={(cfg.cateringIntro && cfg.cateringIntro.trim()) || CATERING_SECTION_INTRO_DEFAULT}
                  bodyClassName={FORMATTED_INTRO_BODY}
                />
              </div>
            </div>
            <div className="mt-10 grid grid-cols-2 items-stretch lg:grid-cols-4 gap-3 sm:gap-4">
              {cateringInvitationBlockEntries(cfg).map(({ key, el }) => (
                <React.Fragment key={key}>{el}</React.Fragment>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── PROGRAM A ZÁBAVA ── */}
      {showEntertainmentSection && (
        <section className={SECTION_Y}>
          <div className="max-w-[800px] mx-auto">
            <h2 className={`${H2} mb-1`}>Program a zábava</h2>
            <OrchidDivider />
            <div className="mt-8 space-y-4">
              {activities.map((act) => (
                <div
                  key={act.id}
                  className="bg-white rounded-[20px] p-6 sm:p-7"
                  style={{ boxShadow: '0px 0px 40px 0px rgba(231,103,103,0.1)' }}
                >
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    {act.start_time && !act.is_all_day && (
                      <span className="text-[#b70b9d] font-bold text-base">{String(act.start_time).slice(0, 5)}</span>
                    )}
                    {act.end_time && !act.is_all_day && (
                      <span className="text-[#b3b3b3] text-sm">– {String(act.end_time).slice(0, 5)}</span>
                    )}
                    {act.is_all_day && (
                      <span className="text-[#808080] text-sm font-medium">Celý den</span>
                    )}
                  </div>
                  <h4 className="font-bold text-[#4d4d4d] text-lg sm:text-xl">{act.title}</h4>
                  {act.description && (
                    <div className="mt-2">
                      <InvitationFormattedText text={act.description} bodyClassName={FORMATTED_CARD_BODY} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── HUDBA ── */}
      {showMusicSection && (
        <section className={SECTION_Y}>
          <div className="max-w-[800px] mx-auto">
            <h2 className={`${H2} mb-1 flex items-center justify-center gap-2 sm:gap-3`}>
              <Music2 size={28} className="text-[#e382d3] shrink-0" aria-hidden />
              Hudba
            </h2>
            <OrchidDivider />
            <div className="mt-8 w-full px-1 sm:px-0">
              <InvitationFormattedText
                justify
                text={(cfg.musicIntro && cfg.musicIntro.trim()) || MUSIC_INTRO_DEFAULT}
                bodyClassName={FORMATTED_INTRO_BODY}
              />
            </div>
            {musicSourceLine && (
              <p className="text-[#666] text-[16px] sm:text-[18px] font-medium leading-relaxed w-full text-justify hyphens-auto px-1 sm:px-0 mt-6">
                {musicSourceLine}
              </p>
            )}
            {spotifyUrlTrim && (
              <div
                className="mt-8 bg-white rounded-[20px] p-6 sm:p-8 text-center"
                style={{ boxShadow: '0px 0px 40px 0px rgba(231,103,103,0.1)' }}
              >
                <div className="mb-6 w-full text-left">
                  <InvitationFormattedText
                    justify
                    text={(cfg.musicPlaylistCta && cfg.musicPlaylistCta.trim()) || MUSIC_PLAYLIST_CTA_DEFAULT}
                    bodyClassName={FORMATTED_CARD_BODY}
                  />
                </div>
                <a
                  href={spotifyUrlTrim}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2.5 bg-[#e8e8e8] hover:bg-[#dadada] text-[#4d4d4d] border border-[#cfcfcf] font-bold text-sm sm:text-base py-3 px-6 sm:py-3.5 sm:px-8 rounded-[10px] transition-colors shadow-sm"
                >
                  <svg
                    className="w-5 h-5 sm:w-[22px] sm:h-[22px] shrink-0 text-[#4d4d4d]"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden
                  >
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.399.099-.78-.18-.869-.539-.091-.42.18-.78.54-.87 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.021zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z" />
                  </svg>
                  Otevřít playlist na Spotify
                </a>
                {!isLikelySpotifyPlaylistUrl(spotifyUrlTrim) && (
                  <p className="text-xs text-[#999] mt-3 break-all">{spotifyUrlTrim}</p>
                )}
              </div>
            )}
            {!spotifyUrlTrim && (cfg.musicPlaylistCta && cfg.musicPlaylistCta.trim()) && (
              <div className="mt-8 w-full px-1 sm:px-0">
                <InvitationFormattedText
                  justify
                  text={cfg.musicPlaylistCta.trim()}
                  bodyClassName={FORMATTED_CARD_BODY}
                />
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── FAQ ── */}
      {cfg.showFaq && notes.length > 0 && (
        <section className={SECTION_Y}>
          <div className="max-w-[800px] mx-auto">
            <h2 className={`${H2} mb-1`}>Důležité informace</h2>
            <OrchidDivider />
            <div className="mt-10 space-y-4">
              {notes.map((note) => (
                <div
                  key={note.id}
                  className="bg-white rounded-[20px] p-6 sm:p-7"
                  style={{ boxShadow: '0px 0px 40px 0px rgba(231,103,103,0.1)' }}
                >
                  <h4 className="font-bold text-[#b70b9d] text-lg sm:text-[25px] mb-2 leading-[1.2]">{note.title}</h4>
                  <InvitationFormattedText text={note.content} bodyClassName={FORMATTED_CARD_BODY} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── INFORMACE PRO UBYTOVANÉ ── */}
      {cfg.showAccommodationInfo && (
        <section className={SECTION_Y}>
          <div className="max-w-[800px] mx-auto">
            <h2 className={`${H2} mb-1 flex items-center justify-center gap-2 sm:gap-3`}>
              <BedDouble size={28} className="text-[#e382d3] shrink-0" aria-hidden />
              Informace pro ubytované
            </h2>
            <OrchidDivider />
            <div
              className="mt-10 bg-white rounded-[20px] p-6 sm:p-8"
              style={{ boxShadow: '0px 0px 40px 0px rgba(231,103,103,0.1)' }}
            >
              <InvitationFormattedText
                text={(cfg.accommodationInfo && cfg.accommodationInfo.trim()) || ACCOMMODATION_INFO_DEFAULT}
                bodyClassName={FORMATTED_CARD_BODY}
              />
              {cfg.accommodationMapsUrl && cfg.accommodationMapsUrl.trim() && (
                <a
                  href={cfg.accommodationMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 mt-6 bg-gradient-to-br from-[#b70b9d] to-[#e382d3] text-white font-bold text-sm sm:text-base py-3 px-6 sm:px-8 rounded-[10px] hover:opacity-95 transition-opacity"
                >
                  <MapPin size={18} /> Otevřít adresu hotelu
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {slug && showGuestFormSection && <GuestInvitationExtrasSection slug={slug} cfg={cfg} />}

      {/* ── FOOTER — Figma: full-width foto + 50 % overlay, jména Allurou ── */}
      <footer className="relative mt-4 overflow-hidden">
        <div className="relative min-h-[280px] sm:min-h-[360px] flex flex-col items-center justify-center py-16 px-6 text-center">
          {ceremonyBg ? (
            <>
              <img src={ceremonyBg} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/50" />
            </>
          ) : (
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(135deg, #b70b9d 0%, #e382d3 55%, #f0bce3 100%)' }}
            />
          )}
          <div className="relative z-10 space-y-4">
            <div className="flex items-center justify-center gap-3">
              <div className="h-px w-14 bg-white/50" />
              <Heart size={22} className="text-white/90" fill="currentColor" />
              <div className="h-px w-14 bg-white/50" />
            </div>
            {cfg.brideName && cfg.groomName && (
              <p className="text-white text-[42px] sm:text-[52px] leading-tight" style={{ fontFamily: "'Allura', cursive" }}>
                {cfg.brideName} &amp; {cfg.groomName}
              </p>
            )}
            {wedding.date && <p className="text-white/85 font-light tracking-[0.25em] text-sm">{weddingDate}</p>}
          </div>
        </div>
        <p className="text-[#b3b3b3] uppercase tracking-[0.35em] text-[10px] py-8 text-center bg-[#fff3fb]">
          Vytvořeno pomocí MarryPlan
        </p>
      </footer>
    </div>
  );
}
